import type { AccountView } from "@kwp/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTRPCClient } from "../../lib/api";
import { accountHealth, syncedAgo } from "../settings/accounts-model";
import { useNow } from "../tasks/use-tasks";

/** How often the shell checks the mailboxes' health while the app is open. */
export const HEALTH_POLL_MS = 5 * 60_000;

export interface HealthNotice {
  accountId: string;
  address: string;
  text: string;
  action: string;
}

/** What the banner says for each mailbox that is not getting its mail (§5); empty: nothing. */
export function healthNotices(accounts: readonly Pick<AccountView, "id" | "address" | "status" | "lastSyncedAt">[], now: Date): HealthNotice[] {
  return accounts.flatMap((a) => {
    const health = accountHealth(a, now);
    if (health === "reconnect") {
      return [{ accountId: a.id, address: a.address, text: `${a.address} needs reconnecting. Its mail has stopped syncing.`, action: "Reconnect" }];
    }
    if (health === "stale" && a.lastSyncedAt !== null) {
      return [{ accountId: a.id, address: a.address, text: `${a.address} has not synced since ${syncedAgo(a.lastSyncedAt, now)}. New mail may be missing.`, action: "Check" }];
    }
    return [];
  });
}

/**
 * The app-wide "a mailbox needs attention" banner (architecture §5: a lost grant never fails
 * silently). Shown on every page but Settings → Mail accounts, where each row says it itself.
 */
export function AccountHealthBanner({ pathname }: { pathname: string }) {
  const client = useTRPCClient();
  const now = useNow();
  // Its own cache entry: polling the shared `accounts.list` from the first page would hand pages
  // that open later a list older than they expect.
  const accounts = useQuery({ queryKey: ["account-health"], queryFn: () => client.accounts.list.query(), staleTime: 60_000, refetchInterval: HEALTH_POLL_MS });
  if (accounts.data === undefined || pathname === "/settings/accounts") return null;
  const notices = healthNotices(accounts.data, now);
  if (notices.length === 0) return null;
  return (
    <div role="alert" className="mx-4 mt-3 flex flex-col gap-2 rounded-lg border border-danger bg-surface p-3 text-sm sm:mx-6">
      {notices.map((n) => (
        <div key={n.accountId} className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <p className="flex-1 text-danger">{n.text}</p>
          <Link
            to="/settings/accounts"
            className="rounded-md bg-accent px-3 py-1 font-medium text-accent-contrast hover:bg-accent-strong"
            aria-label={`${n.action} ${n.address}`}
          >
            {n.action}
          </Link>
        </div>
      ))}
    </div>
  );
}
